import {z as r} from 'zod';

import {
    LocalPackCatalogsEnum,
    LocalPackSearchServiceType,
    LocalPackServicesEnum,
} from '../../service/local-pack/local-pack-type';
import {ShortLocaleNameEnum} from '../locale/locale-context-type';

/**
 * Single help_links row matched by exact `urlPattern` via `useHelpLink(key, true)`.
 *
 * The whole banner setup lives in the `config` JSON on that row:
 * ```jsonc
 * {
 *   "banners": [
 *     {
 *       "enabled": true,                       // on/off switch
 *       "message": {                           // localized text (short locale name -> string)
 *         "ru": "Сбор данных по Google Desktop может занимать больше времени, чем обычно...",
 *         "en": "Google Desktop data collection may take longer than usual..."
 *       },
 *       "targets": [                           // optional: exact catalog/service pairs with the problem
 *         {"catalog": "google", "service": "desktop"},
 *         {"catalog": "yandex", "service": "mobile"}
 *       ]
 *       // a target without `service` covers the whole catalog;
 *       // omit `targets` entirely -> general banner shown on the whole page
 *     }
 *   ]
 * }
 * ```
 */
/** Exact `urlPattern` on help_links (see `isMatchingHelpLink` with `isConfigLink: true`). */
export const LOCAL_PACK_INFO_BANNER_CONFIG_LINK_KEY = 'cp-config-link-local-pack-info-banner';

/** Catalog + service in context on a page (active collection or the selected tab). */
export type LocalPackInfoBannerContextServiceType = Pick<LocalPackSearchServiceType, 'provider' | 'service'>;

const localPackInfoBannerTargetSchema = r.object({
    catalog: r.nativeEnum(LocalPackCatalogsEnum),
    service: r.nativeEnum(LocalPackServicesEnum).optional(),
});

const localPackInfoBannerItemSchema = r.object({
    enabled: r.boolean(),
    message: r.record(r.string(), r.string()),
    targets: r.array(localPackInfoBannerTargetSchema).optional(),
});

export type LocalPackInfoBannerTargetType = r.infer<typeof localPackInfoBannerTargetSchema>;

export const localPackInfoBannerHelpLinkConfigSchema = r.object({
    banners: r.array(localPackInfoBannerItemSchema),
});

export type LocalPackInfoBannerItemType = r.infer<typeof localPackInfoBannerItemSchema>;
export type LocalPackInfoBannerHelpLinkConfigType = r.infer<typeof localPackInfoBannerHelpLinkConfigSchema>;

export function parseLocalPackInfoBannerHelpLinkConfig(raw: unknown): LocalPackInfoBannerHelpLinkConfigType | null {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
        return null;
    }

    const parsed = localPackInfoBannerHelpLinkConfigSchema.safeParse(raw);

    return parsed.success ? parsed.data : null;
}

/**
 * Picks the banner text for the active locale, falling back to English. Not every locale has to be
 * filled in: a Russian user sees the Russian text, any other locale shows its own translation when
 * present and English otherwise. Returns `null` when neither the locale nor English is provided.
 */
export function resolveLocalPackInfoBannerMessage(
    message: LocalPackInfoBannerItemType['message'],
    shortLocaleName: ShortLocaleNameEnum
): string | null {
    return message[shortLocaleName] || message[ShortLocaleNameEnum.en] || null;
}

/**
 * A banner is shown when it is enabled and either has no `targets` (general, whole-page message)
 * or at least one catalog+service pair currently in context matches one of the targets. A target
 * without `service` matches the whole catalog, so each `{catalog, service}` entry pinpoints a
 * single problem spot — `[{catalog: 'google', service: 'desktop'}, {catalog: 'yandex', service:
 * 'mobile'}]` targets exactly Google Desktop and Yandex Mobile.
 */
export function isLocalPackInfoBannerVisible(
    banner: LocalPackInfoBannerItemType,
    activeServices: Array<LocalPackInfoBannerContextServiceType>
): boolean {
    if (!banner.enabled) {
        return false;
    }

    const {targets} = banner;

    if (!targets || targets.length === 0) {
        return true;
    }

    return activeServices.some((active) =>
        targets.some(
            (target) => target.catalog === active.provider && (!target.service || target.service === active.service)
        )
    );
}
